import { useTranslations } from '@vocab/react';
import { Box, IconSearch, Inline, Stack, Text } from 'braid-design-system';

import { TextLink } from 'src/components/NavLink/NavLink';
import { getRefineSearchQuery } from 'src/components/Search/utils/utils';
import { useCountryTitle } from 'src/modules/seo-header/replaceableKeyValues/country';
import { useSearchParams } from 'src/modules/seo-header/search-results-page/search-params';
import { useSearchQueryCorrectionFeatureFlag } from 'src/store/featureFlags/hooks';
import { useSelector } from 'src/store/react';
import {
  selectClearCompanyRefinements,
  selectCompanySuggestion,
  selectResults,
  selectIntentSuggestions,
  selectIsQueryModified,
  selectSearchQuery,
  selectLocationDescription,
} from 'src/store/selectors';
import type {
  SearchViewModelHeader,
  SearchViewModelSuggestions,
} from 'src/types/globals';

import CompanyResults from '../CompanyResults/CompanyResults';
import CompanySuggestion, {
  BroadenCompanySearch,
} from '../CompanySuggestion/CompanySuggestion';
import DidYouMean from '../DidYouMean/DidYouMean';
import SearchResultsDistance from '../SearchResultDistance/SearchResultsDistance';
import SearchResultSummary from '../SearchResultSummary/SearchResultSummary';

import translations from './.vocab';

const paddingY = 'large';

export const SearchResultsHeaderSpacer = () => (
  <Box component="span" display="block" padding={paddingY} aria-hidden>
    <Text size="small">&nbsp;</Text>
  </Box>
);

interface SearchResultsDistanceHeadersProps {
  shouldShowDistanceHeader: boolean | undefined;
  header: SearchViewModelHeader | null | undefined;
  locationSuggestions: SearchViewModelSuggestions | null | undefined;
}

const SearchResultsHeaders = ({
  shouldShowDistanceHeader,
  header,
  locationSuggestions,
}: SearchResultsDistanceHeadersProps) => {
  const searchResultsViewModel = useSelector(selectResults);
  const companySuggestion = useSelector(selectCompanySuggestion);
  const clearCompanyRefinements =
    useSelector(selectClearCompanyRefinements) || false;

  const { searchParams, jobs } = searchResultsViewModel || {};
  const { companyName, keywords: suggestCompanyAsKeywords } =
    companySuggestion?.search || {};

  const isCompanySearch = Boolean(searchParams && searchParams.companysearch);
  const showCompanyResults =
    typeof clearCompanyRefinements === 'object' && !isCompanySearch;

  const { tags } = useSearchParams();
  const isNewToYou = tags === 'new';
  const isSearchQueryCorrectionEnabled = useSearchQueryCorrectionFeatureFlag();
  const isQueryModified = useSelector(selectIsQueryModified);
  const intentSuggestions = useSelector(selectIntentSuggestions);
  const searchQuery = getRefineSearchQuery(useSelector(selectSearchQuery));
  const countryTitle = useCountryTitle();
  const locationDescription =
    useSelector(selectLocationDescription) || countryTitle;
  const { t } = useTranslations(translations);

  return (
    <Box paddingY={paddingY}>
      <Stack space="large">
        <SearchResultSummary key="search-result-summary" />
        {!isNewToYou && (
          <>
            {isSearchQueryCorrectionEnabled &&
              isQueryModified &&
              intentSuggestions?.length &&
              intentSuggestions[0].params.keywords &&
              locationDescription && (
                <Inline space="xsmall" alignY="center">
                  <IconSearch size="xsmall" />
                  <Text
                    data={{ automation: 'searchInsteadForText' }}
                    size="small"
                    tone="secondary"
                  >
                    {t('Search instead for...', {
                      count: intentSuggestions[0].count,
                      keywords: intentSuggestions[0].params.keywords,
                      where: locationDescription,
                      TextLink: (children) => (
                        <TextLink
                          data={{ automation: 'searchInsteadForLink' }}
                          weight="weak"
                          location={{
                            pathname: '/jobs',
                            query: {
                              ...searchQuery,
                              keywords: intentSuggestions[0].params.keywords,
                            },
                          }}
                        >
                          {children}
                        </TextLink>
                      ),
                    })}
                  </Text>
                </Inline>
              )}
            {companyName ? (
              <CompanySuggestion
                companyName={companyName}
                suggestionCount={companySuggestion?.count}
                searchParams={searchParams}
              />
            ) : null}
            {shouldShowDistanceHeader ||
            locationSuggestions ||
            suggestCompanyAsKeywords ||
            showCompanyResults ? (
              <Stack space="medium">
                {suggestCompanyAsKeywords ? (
                  <BroadenCompanySearch
                    keywords={suggestCompanyAsKeywords}
                    suggestionCount={companySuggestion?.count}
                    searchParams={searchParams}
                  />
                ) : null}
                {shouldShowDistanceHeader && !showCompanyResults ? (
                  <SearchResultsDistance message={header} />
                ) : null}
                {locationSuggestions ? (
                  <DidYouMean locations={locationSuggestions} />
                ) : null}
                {showCompanyResults ? (
                  <CompanyResults
                    isCompanySearch={isCompanySearch}
                    clearCompanyRefinements={clearCompanyRefinements}
                    jobs={jobs || []}
                  />
                ) : null}
              </Stack>
            ) : null}
          </>
        )}
      </Stack>
    </Box>
  );
};

export default SearchResultsHeaders;
